import React, { useEffect, useRef, useState } from 'react';
import useIntersectionObserver from '@react-hook/intersection-observer';
import { CircularProgress, styled } from '@mui/material';
import { useLoadHistory } from './hooks';

/**
 * Sentinel element rendered at the top of the legacy reverse list.
 *
 * Because the container uses `flex-direction: column-reverse`, the last DOM
 * child appears visually at the top of the scroll area. When this sentinel
 * enters the viewport the user has scrolled to the top of the conversation,
 * so an older page of history is fetched.
 *
 * A `CircularProgress` spinner is displayed while the load is in flight,
 * giving the user immediate visual feedback that older messages are being
 * retrieved.
 *
 * The `prevIsIntersecting` guard prevents a duplicate load on mount when the
 * list is short enough that the sentinel is immediately visible.
 */
export const HistoryObserver = styled(
	({ className }: { className?: string }) => {
		const [ref, setRef] = useState<HTMLElement | null>(null);
		const { isIntersecting } = useIntersectionObserver(ref, {
			rootMargin: '20px 0px 0px 0px',
			threshold: 1,
		});
		const prevIsIntersecting = useRef<boolean | null>(null);
		const { load, isLoading } = useLoadHistory();

		// Only trigger when transitioning into the viewport (not on initial mount
		// when the sentinel might already be visible).
		useEffect(() => {
			if (isIntersecting && !prevIsIntersecting.current) {
				// Small delay mirrors the original implementation, giving the layout
				// time to stabilise before the history request fires.
				window.setTimeout(load, 100);
			}
			prevIsIntersecting.current = isIntersecting;
		}, [isIntersecting, load]);

		return (
			<div ref={setRef} className={className}>
				{isLoading && <CircularProgress size={24} />}
			</div>
		);
	},
	{ target: 'HistoryObserver-root', label: 'HistoryObserver' },
)({
	height: '3em',
	width: '100%',
	marginTop: '2em',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});
